/* eslint-disable react-hooks/exhaustive-deps */
import {
  getWorkerLogtimeApiErrorMessage,
  useFetchWorkerLogTimes,
  useGeolocation,
  useSubmitWorkerLogTimes,
  useFetchCurrentTime,
  useFetchWorkerLogtimesMonthly,
  resolveCurrentWorkerIdByCompany,
} from "@lhd/hooks";
import { notification } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAttendanceManagementPermission } from "../useAttendanceManagementPermission";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { LOG_TYPE, WORKER_LOGTIME_ATTACHMENT_SIZE_ERROR_MSG } from "./contants";
import type { TrackingTimeShellVariant } from "../components/StyleMain";
import { getCompanyId } from "@lhd/utility";
import { useSelector } from "react-redux";
import { user } from "@lhd/features/users/selector";

dayjs.extend(utc);
dayjs.extend(timezone);

const WORK_LOG_TIMEZONE = "Asia/Tokyo";
export type ReceivedProps = {
  shellVariant?: TrackingTimeShellVariant;
} & Record<string, unknown>;

const getFallbackSubmitLocation = async (): Promise<{
  latitude?: number;
  longitude?: number;
}> => {
  if (!("geolocation" in navigator)) return {};

  const getCurrentPosition = (options: PositionOptions) =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, options);
    });

  try {
    const position =
      (await getCurrentPosition({
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }).catch(() => null)) ??
      (await getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 15000,
        maximumAge: 60000,
      }).catch(() => null));

    if (!position) return {};

    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return {};

    return { latitude, longitude };
  } catch {
    return {};
  }
};

const useTrackingTime = (props: ReceivedProps) => {
  const [typeLogOpenCamera, setTypeLogOpenCamera] = useState<LOG_TYPE>(
    LOG_TYPE.CHECKIN,
  );
  const [isOpenCamera, setIsOpenCamera] = useState(false);
  const [monthHistory, setMonthHistory] = useState<string>(
    dayjs().format("YYYY-MM"),
  );
  const company_id = getCompanyId();
  const auth = useSelector(user);
  const currentWorkerId = resolveCurrentWorkerIdByCompany(auth, company_id);
  const { hasCurrentWorker, canPunchAttendance, canOpenAttendanceManagement } =
    useAttendanceManagementPermission();
  const {
    data,
    isLoading: isLoadingFetchLogTimes,
    isFetching,
    refetch: refetchWorkerLogtimes,
  } = useFetchWorkerLogTimes();
  const { mutateAsync: mutateCheckInAsync, isLoading: isUpdating } =
    useSubmitWorkerLogTimes();
  const geolocation = useGeolocation();
  const { data: currentTimeData } = useFetchCurrentTime();
  const {
    data: historyTrackingTimes,
    isLoading: isLoadingHistoryTrackingTimes,
  } = useFetchWorkerLogtimesMonthly({
    year: dayjs(monthHistory).format("YYYY"),
    month: dayjs(monthHistory).format("MM"),
    perPage: 10000,
    page: 1,
    workerId: currentWorkerId ?? undefined,
  });

  const lastCurrentDatetimeKeyRef = useRef<string | null>(null);
  const serverTimeAnchorRef = useRef({ serverMs: 0, clientMs: 0 });
  const currentDatetimeKey = currentTimeData?.data?.current_datetime ?? "";
  if (
    currentDatetimeKey &&
    currentDatetimeKey !== lastCurrentDatetimeKeyRef.current &&
    currentTimeData?.datetimeWithTimeRes
  ) {
    lastCurrentDatetimeKeyRef.current = currentDatetimeKey;
    serverTimeAnchorRef.current = {
      serverMs: currentTimeData.datetimeWithTimeRes.getTime(),
      clientMs: Date.now(),
    };
  }

  const [clockTick, setClockTick] = useState(0);
  useEffect(() => {
    let intervalId: number | undefined;
    const timeoutId = window.setTimeout(() => {
      setClockTick((n) => n + 1);
      intervalId = window.setInterval(() => setClockTick((n) => n + 1), 1000);
    }, Math.max(0, 1000 - (Date.now() % 1000)));
    return () => {
      window.clearTimeout(timeoutId);
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, []);

  const workerLogtimesData = hasCurrentWorker ? data?.data : undefined;

  const serverNowMs = useMemo(() => {
    const anchor = serverTimeAnchorRef.current;
    const raw =
      anchor.clientMs > 0
        ? anchor.serverMs + (Date.now() - anchor.clientMs)
        : currentTimeData?.datetimeWithTimeRes?.getTime() ?? Date.now();
    return Math.floor(raw / 1000) * 1000;
  }, [clockTick, currentTimeData?.datetimeWithTimeRes]);

  const isAttachmentTooLarge422 = (error: unknown, message: string) => {
    if (error == null || typeof error !== "object" || !("response" in error)) {
      return false;
    }
    const status = (error as { response?: { status?: number } }).response
      ?.status;
    if (status !== 422) return false;
    return (
      message.includes(WORKER_LOGTIME_ATTACHMENT_SIZE_ERROR_MSG) ||
      message.includes("ファイルサイズが上限を超えています")
    );
  };

  const handleSubmitLogTime = async (payload: {
    log_type: LOG_TYPE;
    files?: File[];
  }) => {
    if (!canPunchAttendance) return false;
    const hasLocationReady =
      geolocation.latitude != null &&
      Number.isFinite(geolocation.latitude) &&
      geolocation.longitude != null &&
      Number.isFinite(geolocation.longitude);
    const fallbackLocation = hasLocationReady
      ? {}
      : await getFallbackSubmitLocation();
    const latitude = hasLocationReady
      ? geolocation.latitude
      : fallbackLocation.latitude;
    const longitude = hasLocationReady
      ? geolocation.longitude
      : fallbackLocation.longitude;
    const hasValidLocation =
      latitude != null &&
      Number.isFinite(latitude) &&
      longitude != null &&
      Number.isFinite(longitude);
    const locationPayload = hasValidLocation ? { latitude, longitude } : {};

    try {
      await mutateCheckInAsync({
        payload: {
          ...payload,
          ...locationPayload,
        },
      });
      return true;
    } catch (error) {
      const apiMessage = getWorkerLogtimeApiErrorMessage(error);
      if (isAttachmentTooLarge422(error, apiMessage)) {
        notification.warning({
          message: apiMessage,
        });
        await refetchWorkerLogtimes();
        return true;
      }
      notification.error({
        message: apiMessage,
      });
      return false;
    }
  };

  const disableBtns = useMemo(() => {
    const allDisabled = {
      [LOG_TYPE.CHECKIN]: true,
      [LOG_TYPE.CHECKOUT]: true,
      [LOG_TYPE.BREAK_START]: true,
      [LOG_TYPE.BREAK_END]: true,
      [LOG_TYPE.OTHER]: true,
    };

    if (!hasCurrentWorker) {
      return allDisabled;
    }
    if (workerLogtimesData?.logtime_status) {
      switch (workerLogtimesData.logtime_status) {
        case "not_checked_in":
        case "checked_out":
          return {
            ...allDisabled,
            [LOG_TYPE.CHECKIN]: false,
          };

        case "working":
          return {
            ...allDisabled,
            [LOG_TYPE.CHECKOUT]: false,
            [LOG_TYPE.BREAK_START]: false,
          };

        case "on_break":
          return {
            ...allDisabled,
            [LOG_TYPE.CHECKOUT]: false,
            [LOG_TYPE.BREAK_END]: false,
          };

        default:
          return allDisabled;
      }
    }

    return allDisabled;
  }, [workerLogtimesData?.logtime_status, hasCurrentWorker]);

  const isWorkerLogtimesLoading =
    hasCurrentWorker && (isLoadingFetchLogTimes || isFetching);

  const handleOpenOrCloseCamera = (isOpen?: boolean) => {
    if (isOpen) setIsOpenCamera(true);
    else setIsOpenCamera(false);
  };

  const handleCheckIn = async (log_type: LOG_TYPE) => {
    if (!canPunchAttendance) return;
    if (log_type === LOG_TYPE.CHECKIN || log_type === LOG_TYPE.CHECKOUT) {
      setTypeLogOpenCamera(log_type);
      handleOpenOrCloseCamera(true);
    } else {
      handleSubmitLogTime({ log_type });
    }
  };

  const handleOkCamera = async (files: File[], log_type: LOG_TYPE) => {
    const ok = await handleSubmitLogTime({ log_type, files });
    if (ok) {
      setIsOpenCamera(false);
    }
  };

  return {
    ...props,
    workerLogtimesData,
    currentTimeData,
    serverNowMs,
    handleCheckIn,
    isLoading: isWorkerLogtimesLoading || isUpdating,
    isLoadingFetchLogTimes,
    canPunchAttendance,
    canOpenAttendanceManagement,
    disableBtns,
    isOpenCamera,
    handleOkCamera,
    handleOpenOrCloseCamera,
    typeLogOpenCamera,
    monthHistory,
    setMonthHistory,
    historyTrackingTimes,
    isLoadingHistoryTrackingTimes,
  };
};

export type Props = ReturnType<typeof useTrackingTime>;

export default useTrackingTime;
